import React, { useRef } from "react";
import EnhancedLatexRenderer from "../../../../components/EnhancedLatexRenderer";
import { MessageRenderer } from "../../../../components/MessageWithTableRenderer";
import { MessageRendererNTUST } from "../../../../components/MessageRendererNTUST";
import {
    CopyIcon,
    ThumbsUpFilledIcon,
    ThumbsUpOutlineIcon,
    ThumbsDownActiveIcon,
    ThumbsDownOutlineIcon,
    RetryIcon,
    SendIcon,
    PlusIcon,
    BotAvatarIcon,
} from "./Icons";

// ============================================================================
// BouncingDots — reusable 3-dot loading animation
// ============================================================================

export function BouncingDots({ size = "md", color = "bg-orange-500" }) {
    const sizeClass = size === "sm" ? "w-1.5 h-1.5 md:w-2 md:h-2" : "w-3 h-3";

    return (
        <div className="flex gap-1">
            <div
                className={`${sizeClass} ${color} rounded-full animate-bounce`}
            />
            <div
                className={`${sizeClass} ${color} rounded-full animate-bounce`}
                style={{ animationDelay: "0.1s" }}
            />
            <div
                className={`${sizeClass} ${color} rounded-full animate-bounce`}
                style={{ animationDelay: "0.2s" }}
            />
        </div>
    );
}

// ============================================================================
// ChatInput — shared textarea + send button form
// ============================================================================

export function ChatInput({
    input,
    setInput,
    isLoading,
    language,
    onSubmit,
    onNewChat,
    newChatDisabled = false,
    disabled = false,
    textareaRef: externalRef,
    variant = "full",
}) {
    const internalRef = useRef(null);
    const ref = externalRef || internalRef;

    const isCompact = variant === "compact";

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        const textarea = e.target;
        textarea.style.height = "auto";
        const lineHeight = isCompact ? 20 : 24;
        const maxLines = isCompact ? 3 : 4;
        const maxHeight = lineHeight * maxLines;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = newHeight + "px";
        textarea.style.overflowY =
            textarea.scrollHeight > maxHeight ? "scroll" : "hidden";
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (input.trim() && !isLoading) {
                onSubmit();
            }
        }
    };

    const placeholder = isLoading
        ? language === "en"
            ? "Processing..."
            : "處理中..."
        : language === "en"
          ? "Type your message..."
          : "輸入您的訊息...";

    const textareaStyle = isCompact
        ? { minHeight: "20px", maxHeight: "60px", lineHeight: "20px" }
        : { minHeight: "24px", maxHeight: "96px", lineHeight: "24px" };

    return (
        <form
            className={`flex ${
                isCompact ? "flex-col md:flex-row" : "flex-row"
            } items-center justify-between w-full gap-2 md:gap-3`}
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            {onNewChat && (
                <button
                    type="button"
                    className="w-full md:w-auto bg-blue-500 dark:bg-blue-500/70 hover:bg-blue-500 border border-white/30 py-2 md:py-3 px-4 md:px-6 text-sm font-medium text-white rounded-full transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 backdrop-blur-sm flex items-center justify-center gap-2"
                    onClick={onNewChat}
                    disabled={newChatDisabled}
                >
                    <PlusIcon />
                    {language === "en" ? "New Chat" : "新對話"}
                </button>
            )}

            <div
                className={`flex-1 w-full flex items-end ${
                    isCompact ? "bg-slate-200" : "bg-slate-100"
                } rounded-2xl p-1 backdrop-blur-sm border border-black/30 dark:border-white/30`}
            >
                <textarea
                    ref={ref}
                    className={`flex-1 py-2 md:py-3 px-3 md:px-4 text-sm md:text-base bg-transparent border-none outline-none text-slate-900 placeholder-slate-700 font-medium resize-none no-scrollbar ${
                        isLoading ? "opacity-50 cursor-not-allowed" : ""
                    } ${disabled ? "cursor-not-allowed" : ""}`}
                    rows={1}
                    value={input}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={isLoading || disabled}
                    style={textareaStyle}
                />
                <button
                    className={`border-none p-2 md:p-3 text-white rounded-full transition-all duration-200 flex items-center justify-center h-full ml-1 md:ml-2 ${
                        isLoading || !input.trim()
                            ? "bg-gray-500/50 cursor-not-allowed"
                            : "bg-orange-500/80 hover:bg-orange-500 hover:scale-105"
                    }`}
                    type="submit"
                    disabled={!input.trim() || isLoading}
                >
                    <SendIcon className="w-5 h-5 md:w-7 md:h-7" />
                </button>
            </div>
        </form>
    );
}

// ============================================================================
// MessageActions — copy, thumbs up/down, retry buttons under agent messages
// ============================================================================

export function MessageActions({
    msg,
    messages,
    language,
    isLoading,
    sessionId,
    thumbsUpStates,
    thumbsDownStates,
    onCopy,
    onThumbsUp,
    onThumbsDown,
    onRetry,
}) {
    const handleRetryClick = () => {
        const currentIndex = messages.findIndex((m) => m.id === msg.id);
        let correspondingUserMessage = null;

        for (let i = currentIndex - 1; i >= 0; i--) {
            if (messages[i].role === "user") {
                correspondingUserMessage = messages[i].message;
                break;
            }
        }

        if (!correspondingUserMessage) {
            correspondingUserMessage = messages
                .filter((m) => m.role === "user")
                .slice(-1)[0]?.message;
        }

        if (correspondingUserMessage) {
            onRetry(correspondingUserMessage, msg.id);
        }
    };

    const isThumbsUp = thumbsUpStates[msg.id];
    const isThumbsDown = thumbsDownStates[msg.id];

    const actionBtnBase =
        "self-start px-1.5 md:px-2 py-1 md:py-1.5 rounded-md border border-white/20 transition-all duration-200 flex items-center gap-1 text-xs";
    const actionBtnDefault =
        "bg-black/30 dark:bg-white/10 hover:bg-black/40 dark:hover:bg-white/20 text-white";

    return (
        <div className="flex flex-wrap gap-1 md:gap-2 items-center">
            {/* Copy */}
            <button
                onClick={() => onCopy(msg.message)}
                className={`${actionBtnBase} ${actionBtnDefault} md:px-3 md:py-1.5 gap-1 md:gap-2`}
                title={language === "en" ? "Copy to clipboard" : "複製到剪貼簿"}
            >
                <CopyIcon />
                <span className="hidden sm:inline">
                    {language === "en" ? "Copy" : "複製"}
                </span>
            </button>

            {/* Thumbs Up */}
            <button
                onClick={() => onThumbsUp(msg.id)}
                className={`${actionBtnBase} ${
                    isThumbsUp
                        ? "bg-green-500/20 text-green-400"
                        : actionBtnDefault
                }`}
                title={
                    language === "en"
                        ? isThumbsUp
                            ? "Remove thumbs up"
                            : "Thumbs up"
                        : isThumbsUp
                          ? "移除讚"
                          : "讚"
                }
                type="button"
            >
                {isThumbsUp ? <ThumbsUpFilledIcon /> : <ThumbsUpOutlineIcon />}
            </button>

            {/* Thumbs Down */}
            <button
                onClick={() => {
                    if (!isThumbsDown) {
                        onThumbsDown(
                            sessionId,
                            msg.timestamp,
                            msg.message,
                            msg.id,
                        );
                    }
                }}
                className={`${actionBtnBase} ${
                    isThumbsDown
                        ? "bg-red-500/20 text-red-400"
                        : actionBtnDefault
                }`}
                title={language === "en" ? "Thumbs down" : "不喜歡"}
                type="button"
            >
                {isThumbsDown ? (
                    <ThumbsDownActiveIcon />
                ) : (
                    <ThumbsDownOutlineIcon />
                )}
            </button>

            {/* Retry */}
            <button
                onClick={handleRetryClick}
                disabled={isLoading}
                className={`${actionBtnBase} ${actionBtnDefault} md:gap-2 ${
                    isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={
                    language === "en"
                        ? "Regenerate this response"
                        : "重新生成此回應"
                }
            >
                <RetryIcon />
                <span className="hidden sm:inline">
                    {language === "en" ? "Retry" : "重試"}
                </span>
            </button>
        </div>
    );
}

// ============================================================================
// MessageBubble — renders a single user or agent message
// ============================================================================

export function MessageBubble({
    msg,
    messages,
    language,
    isLoading,
    sessionId,
    thumbsUpStates,
    thumbsDownStates,
    onCopy,
    onThumbsUp,
    onThumbsDown,
    onRetry,
}) {
    if (msg.role === "user") {
        return (
            <div className="flex justify-end mb-2 md:mb-4">
                <div
                    className="max-w-[85%] md:max-w-[70%] bg-orange-500 text-white p-2 md:p-3 rounded-bl-3xl rounded-tl-3xl rounded-br-3xl shadow-sm break-words"
                    style={{
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                    }}
                >
                    <span className="text-xs md:text-sm leading-relaxed">
                        <EnhancedLatexRenderer text={msg.message} />
                    </span>
                </div>
            </div>
        );
    }

    // Agent message
    return (
        <div className="text-xs md:text-sm text-justify flex gap-2 md:gap-3 items-start mb-2 md:mb-4">
            <BotAvatarIcon />

            <div className="w-11/12 flex flex-col gap-1 md:gap-2 mt-2">
                <div
                    className={`rounded-lg rounded-tl-sm break-words ${
                        msg.isError
                            ? "bg-red-500/20 border border-red-500/30"
                            : ""
                    }`}
                    style={{
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                    }}
                >
                    <div
                        className={`font-medium leading-relaxed whitespace-pre-line ${
                            msg.isError
                                ? "text-red-200"
                                : "text-black dark:text-white"
                        }`}
                    >
                        {msg.isLoading ? (
                            <LoadingIndicator message={msg.message} />
                        ) : (
                            <AgentMessageContent
                                msg={msg}
                                language={language}
                            />
                        )}
                    </div>
                </div>

                {!msg.isLoading && (
                    <MessageActions
                        msg={msg}
                        messages={messages}
                        language={language}
                        isLoading={isLoading}
                        sessionId={sessionId}
                        thumbsUpStates={thumbsUpStates}
                        thumbsDownStates={thumbsDownStates}
                        onCopy={onCopy}
                        onThumbsUp={onThumbsUp}
                        onThumbsDown={onThumbsDown}
                        onRetry={onRetry}
                    />
                )}
            </div>
        </div>
    );
}

// --- Private helpers for MessageBubble ---

function LoadingIndicator({ message }) {
    return (
        <div className="flex items-center gap-1 md:gap-2">
            <BouncingDots size="sm" color="bg-orange-300" />
            <span className="text-orange-700 dark:text-orange-300 text-xs md:text-sm">
                <EnhancedLatexRenderer text={message} />
            </span>
        </div>
    );
}

function AgentMessageContent({ msg, language }) {
    if (msg.agentType === "chatproxy_v4") {
        return (
            <MessageRenderer
                text={msg.message}
                actionLinks={msg.actionLinks}
                language={language === "zh" ? "zh" : "en"}
            />
        );
    }

    if (msg.agentType === "chatproxy_NTUST") {
        return (
            <MessageRendererNTUST
                text={msg.message}
                container_actionLinks={msg.container_actionLinks}
            />
        );
    }

    return (
        <MessageRenderer
            text={msg.message}
            language={language === "zh" ? "zh" : "en"}
        />
    );
}
